(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-fbt]';

  function formatMoney(cents, root) {
    const amount = Number(cents || 0);

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(
        amount,
        root.dataset.moneyFormat || window.Shopify.money_format || '₹{{amount}}'
      );
    }

    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: root.dataset.currency || 'INR',
        maximumFractionDigits: 2
      }).format(amount / 100);
    } catch (error) {
      return `₹${(amount / 100).toFixed(2)}`;
    }
  }

  function initFBT(root) {
    if (root.dataset.fbtInitialized === 'true') return;
    root.dataset.fbtInitialized = 'true';

    const cards = Array.from(root.querySelectorAll('[data-fbt-card]'));
    const totalEl = root.querySelector('[data-fbt-total]');
    const compareEl = root.querySelector('[data-fbt-compare]');
    const countEl = root.querySelector('[data-fbt-count]');
    const savingsEl = root.querySelector('[data-fbt-savings-amount]');
    const addButton = root.querySelector('[data-fbt-add]');
    const messageEl = root.querySelector('[data-fbt-message]');

    const discount = Math.max(
      0,
      Math.min(50, Number(root.dataset.discount || 15))
    );

    if (!cards.length || !addButton) return;

    /*
     * Read the live selected variant from the CSPP product form.
     * This avoids using the original variant if the customer changes
     * the variant after the page has loaded.
     */
    function getCurrentVariant() {
      const productSection = document.querySelector('[data-cspp]');
      const variantInput = productSection?.querySelector('[data-cspp-variant-id]');
      const variantsScript = productSection?.querySelector('[data-cspp-variants]');

      const selectedId = Number(
        variantInput?.value || root.dataset.currentVariant || 0
      );

      let variantData = null;

      if (variantsScript) {
        try {
          const variants = JSON.parse(variantsScript.textContent || '[]');
          variantData = variants.find(
            variant => Number(variant.id) === selectedId
          ) || null;
        } catch (error) {
          console.error('FBT: Could not read CSPP variants.', error);
        }
      }

      return {
        id: selectedId,
        price: Number(variantData?.price ?? root.dataset.currentPrice ?? 0),
        comparePrice: Number(
          variantData?.compare_at_price ??
          variantData?.price ??
          root.dataset.currentPrice ??
          0
        ),
        available: variantData
          ? Boolean(variantData.available)
          : root.dataset.currentAvailable === 'true'
      };
    }

    function selectedCards() {
      return cards.filter(card => {
        const checkbox = card.querySelector('[data-fbt-select]');
        return checkbox && checkbox.checked && !checkbox.disabled;
      });
    }

    function selectedCount() {
      // Current product is always included, even though it has no card.
      return 1 + selectedCards().length;
    }

    function refresh() {
      const current = getCurrentVariant();

      let subtotal = current.price;
      let compareSubtotal = Math.max(current.price, current.comparePrice);

      cards.forEach(card => {
        const checkbox = card.querySelector('[data-fbt-select]');
        const selected = checkbox && checkbox.checked && !checkbox.disabled;

        card.classList.toggle('is-unselected', !selected);

        if (selected) {
          const price = Number(card.dataset.price || 0);
          const comparePrice = Number(card.dataset.comparePrice || price);

          subtotal += price;
          compareSubtotal += Math.max(price, comparePrice);
        }
      });

      const discountedTotal = Math.round(
        subtotal * (100 - discount) / 100
      );

      const savings = subtotal - discountedTotal;
      const count = selectedCount();

      if (totalEl) {
        totalEl.textContent = formatMoney(discountedTotal, root);
      }

      if (compareEl) {
        compareEl.textContent = formatMoney(compareSubtotal, root);
        compareEl.hidden = compareSubtotal <= discountedTotal;
      }

      if (countEl) countEl.textContent = String(count);

      if (savingsEl) {
        savingsEl.textContent = formatMoney(savings, root);
      }

      addButton.textContent =
        `Add Selected ${count} Item${count === 1 ? '' : 's'} To Cart`;

      addButton.disabled = !current.id || !current.available;
    }

    root.querySelectorAll('[data-fbt-select]').forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        if (messageEl) messageEl.textContent = '';
        refresh();
      });
    });

    /*
     * Watch CSPP's selected variant input.
     * Also refresh after variant-picker changes.
     */
    const productSection = document.querySelector('[data-cspp]');
    const variantInput = productSection?.querySelector('[data-cspp-variant-id]');

    if (variantInput) {
      const observer = new MutationObserver(refresh);
      observer.observe(variantInput, {
        attributes: true,
        attributeFilter: ['value']
      });

      variantInput.addEventListener('change', refresh);
      productSection.addEventListener('change', event => {
        if (
          event.target.matches(
            'select, input[type="radio"], [data-cspp-variant-id]'
          )
        ) {
          window.requestAnimationFrame(refresh);
        }
      });

      productSection.addEventListener('variant:change', refresh);
    }

    /*
     * Refresh cart drawer contents and header count, then open
     * the existing custom drawer (#CSC-CustomCart).
     */
    async function openCustomCartDrawer() {
      const rootUrl = window.Shopify?.routes?.root || '/';

      let cart = null;

      try {
        const response = await fetch(`${rootUrl}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });

        if (response.ok) cart = await response.json();
      } catch (error) {
        console.error('FBT: Could not refresh cart.', error);
      }

      if (cart) {
        document.dispatchEvent(
          new CustomEvent('cart:refresh', { detail: { cart } })
        );

        document.querySelectorAll('[data-csa-cart-count]').forEach(badge => {
          badge.textContent = cart.item_count;
          badge.hidden = cart.item_count < 1;
        });

        document.querySelectorAll('[data-csc-cart-count]').forEach(badge => {
          badge.textContent = cart.item_count;
          badge.hidden = cart.item_count < 1;
        });
      }

      const customDrawer = document.getElementById('CSC-CustomCart');

      /*
       * The existing custom drawer listens for clicks on these header
       * selectors and opens itself, then refreshes its cart.
       */
      const cartTrigger = document.querySelector(
        '#CSA-header-cart, .CSA-header__cart, [data-csc-open-cart]'
      );

      if (cartTrigger) {
        cartTrigger.click();
        return;
      }

      /*
       * Fallback: open the same custom drawer directly if the header
       * cart trigger is not present on this template.
       */
      if (customDrawer) {
        customDrawer.classList.add('is-open');
        customDrawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('csc-cart-open');
      } else {
        console.warn(
          'FBT: #CSC-CustomCart was not found. Check that the custom cart drawer section is loaded.'
        );
      }
    }

    addButton.addEventListener('click', async () => {
      const current = getCurrentVariant();

      if (!current.id || !current.available) {
        if (messageEl) {
          messageEl.textContent = 'The selected current product variant is unavailable.';
        }
        refresh();
        return;
      }

      const items = [
        {
          id: current.id,
          quantity: 1
        },
        ...selectedCards().map(card => ({
          id: Number(card.dataset.variantId),
          quantity: 1
        }))
      ];

      if (items.some(item => !item.id || !Number.isFinite(item.id))) {
        if (messageEl) {
          messageEl.textContent = 'A selected product variant is invalid.';
        }
        return;
      }

      addButton.disabled = true;
      const originalText = addButton.textContent;
      addButton.textContent = 'Adding to cart…';

      if (messageEl) messageEl.textContent = '';

      try {
        const rootUrl = window.Shopify?.routes?.root || '/';

        const response = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify({ items })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.description ||
            result.message ||
            'Unable to add the selected products. Please try again.'
          );
        }

        if (messageEl) {
          messageEl.textContent = 'Items added to cart.';
        }

        await openCustomCartDrawer();

        addButton.textContent = 'Added to Cart ✓';

        window.setTimeout(() => {
          addButton.textContent = originalText;
          refresh();
        }, 1800);
      } catch (error) {
        if (messageEl) {
          messageEl.textContent =
            error.message || 'Something went wrong. Please try again.';
        }

        addButton.textContent = originalText;
        refresh();
      } finally {
        addButton.disabled = false;
      }
    });

    refresh();
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initFBT);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();